import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import { useMutation, useQuery, type QueryKey } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { billing } from '@/services/billing';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { readInputValue } from '@/utils/dom';
import {
  addressFromAccount,
  applyAddressChange,
  persistAccountAddress,
  visibleBillingErrors,
} from '../utils/address-form-helpers';
import { billingFieldErrors, firstBillingError } from '../utils/credit-card-form-validation';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { getCurrentPlan } from '../utils/current-plan-label';
import { isZipCodeIgnored } from '../utils/address-options';
import { hasTaxableAddress, TAX_DEBOUNCED_FIELDS, TAX_LOOKUP_DEBOUNCE_MS } from '../utils/tax-lookup-rules';
import { validateBillingAddress } from '../utils/address-validation';
import { useAbortableTaxLookup } from './use-abortable-tax-lookup';
import { UpdateAddressModal } from '../components/update-address-modal';
import { parseQuantityInput, type CreditsPurchaseConfig } from '../utils/credits-purchase-config';
import type { CreditsPurchasePageName } from '../utils/credits-purchase-page-name';
import {
  balanceLimitLabel,
  computeTotals,
  effectiveMaxQuantity,
  quantityError as computeQuantityError,
  reportedTotalCredits,
  submitTooltipMessage,
  type CreditsPurchaseTotals,
} from '../utils/credits-purchase-form-helpers';
import { useBillingEditGuard } from './use-billing-edit-guard';
import { useCreditsPurchaseSubmit } from './use-credits-purchase-submit';
import { type StripeLoadState, useStripeLoad } from './use-stripe-load';

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** The four address fields a tax-rate request is made of. */
type TaxLookupTarget = Pick<CreditCardFormAddress, 'country' | 'state' | 'zipCode' | 'addressLine1'>;

function taxLookupTarget(address: CreditCardFormAddress): TaxLookupTarget {
  return {
    country: address.country,
    state: address.state,
    zipCode: address.zipCode,
    addressLine1: address.addressLine1,
  };
}

// Whether the move to this target came from typing, so its request waits out the debounce first. The
// first lookup of the form has no previous target: that address was chosen, not typed, so it goes now.
function isTypedChange(previous: TaxLookupTarget | null, next: TaxLookupTarget): boolean {
  return previous != null && TAX_DEBOUNCED_FIELDS.some((field) => previous[field] !== next[field]);
}

// The billing-service atoms the form reads. Bundled so the orchestrator stays under the hook budget.
function useCreditsPurchaseBillingState() {
  return {
    hasCard: useStore(billing.hasCard),
    isOrdwayCustomer: useStore(billing.isOrdwayCustomer),
    isPremiumTrial: useStore(billing.isPremiumTrial),
    isTrial: useStore(billing.isTrial),
    isSubscriptionActive: useStore(billing.isSubscriptionActive),
    planType: useStore(billing.planType),
  };
}

// Fetches the Ordway saved card when the saved-card path is active. Backed by a query so the
// loading/error states are managed automatically. Non-Ordway saved cards come from already-loaded
// subscription state, so they need no fetch.
function useOrdwaySavedCard(useSavedCard: boolean, isOrdwayCustomer: boolean, hasCard: boolean) {
  const { account } = useAuth();
  const cardDetails = useStore(billing.cardDetails);
  const shouldFetch = useSavedCard && isOrdwayCustomer;

  const query = useQuery({
    queryKey: ['ordwayCardDetails', account.id],
    queryFn: () => billing.fetchOrdwayCardDetails.perform(),
    enabled: shouldFetch,
    staleTime: 0,
  });

  return {
    cardDetails,
    isCardLoading: shouldFetch && query.isLoading,
    isCardError: shouldFetch && query.isError,
    hasCard,
  };
}

/**
 * Drives the billing tax-rate store from the current address, returning the rate fraction and whether a
 * lookup is in flight. Tax applies on both the new-card and saved-card paths (the saved-card path uses
 * the account address) so the "including tax" total is always accurate.
 *
 * The invariant: a rate is reported only for the address on screen. So the query is keyed on the live
 * address — never on a delayed copy of it — and the debounce for typed fields runs inside the query
 * function rather than before it. That is what lets `isFetching` also cover the "still typing, nothing
 * sent yet" window: submit is blocked from the keystroke, not just from the request. Keying on a delayed
 * address instead left up to a debounce of the live address priced with the previous address's tax, and
 * submit open, because both the completeness gate and the key still described the old address.
 *
 * Exported for the supersede tests; the form reads it through `useCreditsPurchaseForm`.
 */
/** One place for the key shape, so the abort site removes exactly the entry the run is registered under. */
function creditsPurchaseTaxRateQueryKey(target: TaxLookupTarget): QueryKey {
  return ['creditsPurchaseTaxRate', target.country, target.state, target.zipCode, target.addressLine1];
}

export type CreditsPurchaseTaxRate = {
  /** The rate fraction for the address on screen, or undefined when nothing prices it. */
  rate: number | undefined;
  isFetching: boolean;
};

export function useCreditsPurchaseTaxRate(address: CreditCardFormAddress): CreditsPurchaseTaxRate {
  const taxRate = useStore(billing.taxRate);
  const target = taxLookupTarget(address);
  const isComplete = hasTaxableAddress(address);

  // Supersedes the lookup started for the previous address. `updateTaxRate` writes the rate inside the
  // billing service and `enqueueTask` runs serially with no cancellation, so aborting the signal is the
  // only thing that stops an older run from writing over a rate a newer address has already cleared or
  // replaced. React Query cancels neither a running `queryFn` whose key changed nor one whose query has
  // become disabled, so without this the in-flight lookup landed after `resetTaxRate` and priced an
  // untaxable address with the previous address's tax — with submit no longer blocked, because
  // `isFetching` below is gated on `isComplete`. Same guard, and the same `signal`, as checkout's
  // `useTaxRateRefresh`.
  // Also drops the superseded run's cache entry, so an address returning to it starts a fresh lookup
  // instead of being deduped onto a run that can no longer write a rate. See `useAbortableTaxLookup`.
  const lookup = useAbortableTaxLookup();
  // The target the last lookup was started for, which is what says whether this one was typed. Held in a
  // ref because it decides how a request runs, not what the form renders.
  const lastTargetRef = useRef<TaxLookupTarget | null>(null);

  const queryKey = creditsPurchaseTaxRateQueryKey(target);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      // Abort from in here rather than from a render or an effect: a change that resolves to the key
      // already in flight never reaches this point (React Query dedupes it), and it must not have the
      // lookup it is waiting on cancelled out from under it.
      const signal = lookup.begin(queryKey);

      const isTyped = isTypedChange(lastTargetRef.current, target);
      lastTargetRef.current = target;
      if (isTyped) {
        await sleep(TAX_LOOKUP_DEBOUNCE_MS);
        if (signal.aborted) {
          return null;
        }
      }

      await billing.updateTaxRate.perform({ ...target, signal });
      // Read the store rather than the awaited value: a superseded run skips its write, so the newer
      // run's rate is what belongs to the address on screen. The caller reads the store too.
      return billing.taxRate.get()?.rate ?? null;
    },
    enabled: isComplete,
    // A rate belongs to one address and one edit: never serve it from cache, and never keep it once the
    // address has moved on.
    staleTime: 0,
    gcTime: 0,
  });

  // Clear any stored rate once the address is no longer complete, and on unmount. Abort first, or the
  // lookup this reset supersedes writes its rate back afterwards.
  useEffect(() => {
    if (!isComplete) {
      lookup.abort();
      billing.resetTaxRate();
    }
  }, [isComplete, lookup]);
  useEffect(
    () => () => {
      lookup.abort();
      billing.resetTaxRate();
    },
    [lookup],
  );

  // Report no rate for an address we do not price, without waiting for the reset effect to run: the
  // totals must never show tax the address on screen cannot carry, not even for one paint.
  return { rate: isComplete ? taxRate?.rate : undefined, isFetching: isComplete && query.isFetching };
}

export type CreditsPurchaseFormView = {
  quantity: number;
  useSavedCard: boolean;
  saveCard: boolean;
  name: string;
  address: CreditCardFormAddress;
  totals: CreditsPurchaseTotals;
  quantityError: string | undefined;
  fieldErrors: ReturnType<typeof billingFieldErrors>;
  visibleErrors: ReturnType<typeof billingFieldErrors>;
  submitDisabled: boolean;
  submitBlocked: boolean;
  isCardComplete: boolean;
  submitTooltip: string;
  isSubmitting: boolean;
  /** Whether the user may edit billing; gates the address fields. */
  canEdit: boolean;
  hasSavedCard: boolean;
  savedCard: ReturnType<typeof useOrdwaySavedCard>;
  /** Whether Stripe.js loaded; a failure replaces the card fields with the ad-blocker message. */
  stripeLoad: StripeLoadState;
  minQuantity: number;
  maxQuantity: number;
  canEditZip: boolean;
  onQuantityInput: (event: Event) => void;
  onQuantityBlur: () => void;
  onQuantityChange: (quantity: number) => void;
  /** Packs mode: selects a pack by its credit count. */
  onPackSelect: (size: number) => void;
  onNameInput: (value: string) => void;
  onAddressChange: (field: keyof CreditCardFormAddress, value: string) => void;
  onFieldBlur: (field: string) => void;
  onSaveCardChange: (checked: boolean) => void;
  onTogglePaymentMethod: () => void;
  onSubmit: () => void;
  /** Consumed by `<CardFields onChange>` to report Stripe card completeness. */
  setIsCardComplete: (complete: boolean) => void;
};

/**
 * The state, derived values, and handlers for the shared Stripe credit-purchase form. Composes the
 * billing-store, saved-card, tax-rate, and submit hooks so the form component stays presentational.
 */
// The residual complexity is hook composition + assembling the view model, not branching logic; the
// branchy pieces live in the pure helpers and the submit module.
// fallow-ignore-next-line complexity
export function useCreditsPurchaseForm(
  config: CreditsPurchaseConfig,
  currentBalanceCents: number | undefined,
  options: {
    /** The surface that opened the form, reported with every event it fires. */
    pageName: CreditsPurchasePageName;
    /** The quantity to open on. Held inside the config range and the balance cap. */
    initialQuantity?: number;
    initialSaveCard?: boolean;
    onSuccess: (info: { savedCard: boolean }) => void;
    onProcessingChange?: (isProcessing: boolean) => void;
  },
): CreditsPurchaseFormView {
  const { account } = useAuth();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const modals = useModals();
  const stripe = useStripe();
  const elements = useElements();
  const editGuard = useBillingEditGuard();
  const submitCredits = useCreditsPurchaseSubmit();
  const { mutateAsync: updateAccountAddress } = useUpdateAccountAddress({ accountId: account.id });
  const billingState = useCreditsPurchaseBillingState();

  // Deliberately unclamped. A preset larger than the remaining headroom opens on the amount that was
  // asked for, and the quantity field states the cap — clamping it down to the headroom would answer
  // a click on Add $50 with $30 and no reason for it.
  const [quantity, setQuantity] = useState(options.initialQuantity ?? config.defaultQuantity);
  const [useSavedCard, setUseSavedCard] = useState(billingState.hasCard);
  const [saveCard, setSaveCard] = useState(options.initialSaveCard ?? false);
  const [name, setName] = useState('');
  const [address, setAddress] = useState<CreditCardFormAddress>(() => addressFromAccount(account));
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());

  const savedCard = useOrdwaySavedCard(useSavedCard, billingState.isOrdwayCustomer, billingState.hasCard);
  const stripeLoad = useStripeLoad();
  const { rate: taxRateFraction, isFetching: isTaxFetching } = useCreditsPurchaseTaxRate(address);

  const currentPlan = getCurrentPlan(
    billingState.isPremiumTrial,
    billingState.isTrial,
    billingState.isSubscriptionActive,
    billingState.planType,
  );

  const trackedQuantityRef = useRef(quantity);
  const purchaseCompletedRef = useRef(false);
  // Whether the user touched any field this session; reported with `modal_closed`. A ref so setting
  // it never re-renders.
  const hadInteractedRef = useRef(false);
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    void trackRef.current('creditsPurchase', 'modalOpened', {
      'Credit Type': config.creditType,
      'Account Id': account.id,
      'Current Plan': currentPlan,
      page_name: options.pageName,
    });
    return () => {
      billing.pollForCreditsPurchaseTask.cancelAll();
      if (!purchaseCompletedRef.current) {
        void trackRef.current('creditsPurchase', 'modalClosed', {
          'Credit Type': config.creditType,
          'Had Interaction': hadInteractedRef.current,
          page_name: options.pageName,
        });
      }
    };
    // Fire once on mount/unmount; values are captured through refs / stable for the modal's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/unmount-only analytics
  }, []);

  const effectiveMax = effectiveMaxQuantity(config, currentBalanceCents);
  const limitLabel = balanceLimitLabel(config, effectiveMax);
  // A pack selection is one of the config's own sizes, so it cannot be invalid.
  const quantityError =
    config.packs == null ? computeQuantityError(quantity, config, effectiveMax, limitLabel) : undefined;
  const totals = useMemo(() => computeTotals(quantity, config, taxRateFraction), [quantity, config, taxRateFraction]);

  const fieldErrors = useSavedCard ? {} : billingFieldErrors(name, address);
  const firstError = firstBillingError(fieldErrors);
  // Stripe can't report completeness in test mode (plain inputs), so treat the card as complete then.
  const allComplete = useSavedCard || IS_STRIPE_TEST_MODE || isCardComplete;

  const trackFailed = useCallback(
    (errorType: string) => {
      void track('creditsPurchase', 'purchaseFailed', {
        'Credit Type': config.creditType,
        'Account Id': account.id,
        Quantity: quantity,
        'Total Price Cents': totals.totalPriceCents,
        'Error Type': errorType,
        'Billing System': 'stripe',
        page_name: options.pageName,
      });
    },
    [track, config.creditType, options.pageName, account.id, quantity, totals.totalPriceCents],
  );

  // The whole submit (address validation, correction modal, charge, and poll) runs inside the
  // mutation, so `isPending` disables the button across the entire flow — closing the window where a
  // second click could start a second charge during address validation.
  const submitMutation = useMutation({
    mutationFn: (submitArgs: RunSubmitArgs) => runCreditsPurchaseSubmit(submitArgs),
  });
  const isSubmitting = submitMutation.isPending;

  const submitBlocked =
    editGuard.isDisabled ||
    isSubmitting ||
    isTaxFetching ||
    savedCard.isCardLoading ||
    savedCard.isCardError ||
    // Never let a charge start before Stripe.js is usable: the PaymentIntent is created server-side, so
    // an ad-blocked submit leaves an unpaid invoice behind.
    stripeLoad.isLoading ||
    stripeLoad.isError;
  const submitDisabled = submitBlocked || firstError != null || quantityError != null || !allComplete;
  const submitTooltip = editGuard.isDisabled
    ? editGuard.disabledMessage
    : submitTooltipMessage({
        fieldError: firstError,
        quantityError,
        allComplete,
        isStripeLoadError: stripeLoad.isError,
        isTaxRateLoading: isTaxFetching,
      });

  const visibleErrors = useMemo(() => visibleBillingErrors(fieldErrors, touched), [fieldErrors, touched]);

  const onSubmit = useCallback(() => {
    if (submitDisabled) {
      return;
    }
    submitMutation.mutate({
      config,
      pageName: options.pageName,
      quantity,
      totals,
      useSavedCard,
      saveCard,
      address,
      name,
      accountId: account.id,
      accountName: account.name,
      currentPlan,
      stripe,
      cardNumberElement: elements?.getElement(CardNumberElement) ?? null,
      updateAccountAddress,
      modals,
      snackbars,
      submitCredits,
      track,
      trackFailed,
      setAddress,
      markCompleted: () => {
        purchaseCompletedRef.current = true;
      },
      onSuccess: options.onSuccess,
      onProcessingChange: options.onProcessingChange,
    });
  }, [
    submitDisabled,
    submitMutation,
    config,
    options.pageName,
    quantity,
    totals,
    useSavedCard,
    saveCard,
    address,
    name,
    account.id,
    account.name,
    currentPlan,
    stripe,
    elements,
    updateAccountAddress,
    modals,
    snackbars,
    submitCredits,
    track,
    trackFailed,
    options.onSuccess,
    options.onProcessingChange,
  ]);

  return {
    quantity,
    useSavedCard,
    saveCard,
    name,
    address,
    totals,
    quantityError,
    fieldErrors,
    visibleErrors,
    submitDisabled,
    submitBlocked,
    isCardComplete: allComplete,
    submitTooltip,
    isSubmitting,
    canEdit: editGuard.canEdit,
    hasSavedCard: billingState.hasCard,
    savedCard,
    stripeLoad,
    minQuantity: config.minQuantity,
    maxQuantity: effectiveMax,
    canEditZip: !isZipCodeIgnored(address.country),
    onPackSelect: (size) => {
      hadInteractedRef.current = true;
      setQuantity(size);
      // A pack selection is a quantity change; track it like the number-input
      // blur does. The pack price is looked up directly — `totals` still
      // reflects the previous selection in this tick.
      if (size !== trackedQuantityRef.current) {
        trackedQuantityRef.current = size;
        const pack = config.packs?.find((candidate) => candidate.size === size);
        void track('creditsPurchase', 'quantityChanged', {
          'Credit Type': config.creditType,
          Quantity: size,
          'Total Price Cents': pack?.priceCents ?? size * config.pricePerUnitCents,
          'Billing System': 'stripe',
          page_name: options.pageName,
        });
      }
    },
    onQuantityInput: (event) => {
      const value = parseQuantityInput(readInputValue(event));
      if (value != null) {
        hadInteractedRef.current = true;
        setQuantity(value);
      }
    },
    onQuantityChange: (value) => {
      hadInteractedRef.current = true;
      setQuantity(value);
    },
    onQuantityBlur: () => {
      if (quantity !== trackedQuantityRef.current) {
        trackedQuantityRef.current = quantity;
        void track('creditsPurchase', 'quantityChanged', {
          'Credit Type': config.creditType,
          Quantity: quantity,
          'Total Price Cents': totals.totalPriceCents,
          'Billing System': 'stripe',
          page_name: options.pageName,
        });
      }
    },
    onNameInput: (value) => {
      hadInteractedRef.current = true;
      setName(value);
    },
    onAddressChange: (field, value) => {
      hadInteractedRef.current = true;
      setAddress((current) => applyAddressChange(current, field, value));
    },
    onFieldBlur: (field) => setTouched((current) => new Set(current).add(field)),
    onSaveCardChange: (checked) => {
      hadInteractedRef.current = true;
      setSaveCard(checked);
    },
    onTogglePaymentMethod: () => {
      const next = !useSavedCard;
      hadInteractedRef.current = true;
      setUseSavedCard(next);
      setSaveCard(false);
      if (next) {
        setAddress(addressFromAccount(account));
      } else {
        setIsCardComplete(false);
      }
      void track('creditsPurchase', 'paymentMethodChanged', {
        'Credit Type': config.creditType,
        'Payment Method': next ? 'saved_card' : 'new_card',
        page_name: options.pageName,
      });
    },
    onSubmit,
    setIsCardComplete: (complete) => {
      hadInteractedRef.current = true;
      setIsCardComplete(complete);
    },
  };
}

type RunSubmitArgs = {
  config: CreditsPurchaseConfig;
  pageName: CreditsPurchasePageName;
  quantity: number;
  totals: CreditsPurchaseTotals;
  useSavedCard: boolean;
  saveCard: boolean;
  address: CreditCardFormAddress;
  name: string;
  accountId: number;
  accountName: string;
  currentPlan: string;
  stripe: ReturnType<typeof useStripe>;
  cardNumberElement: StripeCardNumberElement | null;
  updateAccountAddress: ReturnType<typeof useUpdateAccountAddress>['mutateAsync'];
  modals: ReturnType<typeof useModals>;
  snackbars: ReturnType<typeof useSnackbars>;
  submitCredits: ReturnType<typeof useCreditsPurchaseSubmit>;
  track: ReturnType<typeof useAnalytics>['track'];
  trackFailed: (errorType: string) => void;
  setAddress: (address: CreditCardFormAddress) => void;
  markCompleted: () => void;
  onSuccess: (info: { savedCard: boolean }) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
};

/**
 * The effective "save this card" decision at the submit boundary. The checkbox only exists when the
 * config allows it and the user is entering a new card, so a stale `true` (a config change, or a toggle
 * to the saved card that missed the reset) must not reach the charge: `saveCard` drives the address
 * correction check, the account address write, and `save_card` on the wire, so all three conditions are
 * re-derived here at the submit boundary rather than trusted from the checkbox state.
 */
export function effectiveSaveCard(args: { allowSaveCard: boolean; useSavedCard: boolean; saveCard: boolean }): boolean {
  return args.allowSaveCard && !args.useSavedCard && args.saveCard;
}

// Runs the correction check, the submit analytics, then delegates the charge to the submit hook.
async function runCreditsPurchaseSubmit(args: RunSubmitArgs): Promise<void> {
  const saveCard = effectiveSaveCard({
    allowSaveCard: args.config.allowSaveCard,
    useSavedCard: args.useSavedCard,
    saveCard: args.saveCard,
  });

  let submitAddress = args.address;
  if (saveCard) {
    const corrected = await resolveCorrectedAddress(args, submitAddress);
    if (corrected === 'aborted') {
      return;
    }
    submitAddress = corrected;
    args.setAddress(corrected);
  }

  args.track('creditsPurchase', 'purchaseSubmitted', {
    'Credit Type': args.config.creditType,
    'Account Id': args.accountId,
    'Current Plan': args.currentPlan,
    Quantity: args.quantity,
    'Total Price Cents': args.totals.totalPriceCents,
    'Tax Amount Cents': args.totals.taxAmountCents,
    'Total With Tax Cents': args.totals.totalWithTaxCents,
    'Payment Method': args.useSavedCard ? 'saved_card' : 'new_card',
    'Save Card': saveCard,
    'Total Credits': reportedTotalCredits(args.config, args.totals),
    'Billing System': 'stripe',
    page_name: args.pageName,
  });

  await args.submitCredits({
    config: args.config,
    quantity: args.quantity,
    totals: args.totals,
    useSavedCard: args.useSavedCard,
    saveCard,
    address: submitAddress,
    name: args.name,
    stripe: args.stripe,
    cardNumberElement: args.cardNumberElement,
    updateAccountAddress: (next) => persistAccountAddress(args.updateAccountAddress, next),
    onProcessingChange: args.onProcessingChange,
    onSuccess: (info) => {
      args.markCompleted();
      args.onSuccess(info);
    },
    onTrackSucceeded: () => {
      args.track('creditsPurchase', 'purchaseSucceeded', {
        'Credit Type': args.config.creditType,
        'Account Id': args.accountId,
        Quantity: args.quantity,
        'Total Price Cents': args.totals.totalPriceCents,
        'Tax Amount Cents': args.totals.taxAmountCents,
        'Total With Tax Cents': args.totals.totalWithTaxCents,
        'Total Credits': reportedTotalCredits(args.config, args.totals),
        'Billing System': 'stripe',
        page_name: args.pageName,
      });
    },
    onTrackFailed: args.trackFailed,
  });
}

// Validates the address; on a suggested correction opens the modal and returns the chosen address, or
// 'aborted' when the user dismisses it (a snackbar is shown). No correction -> the input address.
async function resolveCorrectedAddress(
  args: Pick<RunSubmitArgs, 'accountId' | 'modals' | 'snackbars' | 'config'>,
  input: CreditCardFormAddress,
): Promise<CreditCardFormAddress | 'aborted'> {
  const validation = await validateBillingAddress(args.accountId, input);
  if (validation?.correctedAddress == null) {
    return input;
  }

  const modalId = args.modals.open(UpdateAddressModal, {
    inputAddress: input,
    correctedAddress: validation.correctedAddress,
  });
  const result = await args.modals.waitForClose(modalId);
  if (result.reason === 'confirm' && result.data != null) {
    return result.data;
  }

  args.snackbars.create({
    id: `${args.config.snackbarPrefix}.has-corrected-address`,
    variant: 'critical',
    message: 'We found a possible correction for your address. Select the current address or corrected address.',
  });
  return 'aborted';
}
